import React from "react";
import styled from "styled-components";
import { Link } from "react-scroll";
import { Link as RouterLink, useLocation } from "react-router-dom";
// Icons
import { FaRocket } from "react-icons/fa6";
// Assets
import CloseIcon from "../../assets/svg/CloseIcon";
import LogoIcon from "../../assets/optimized/logo.webp";

export default function Sidebar({ sidebarOpen, toggleSidebar }) {
  const location = useLocation();
  
  // Check if we're on the landing page
  const isLandingPage = location.pathname === '/';
  
  // Navigation component
  const SidebarNavItem = ({ to, children }) => {
    if (isLandingPage) {
      // Use react-scroll Link for landing page
      return (
        <li className="semiBold font15 pointer">
          <Link
            onClick={() => toggleSidebar()}
            activeClass="active"
            className="whiteColor"
            style={{ padding: "10px 15px" }}
            to={to}
            spy={true}
            smooth={true}
            offset={-60}
          >
            {children}
          </Link>
        </li>
      );
    } else {
      // Use Router Link to navigate back to landing page with hash
      return (
        <li className="semiBold font15 pointer">
          <RouterLink
            to={`/#${to}`}
            onClick={() => toggleSidebar()}
            style={{ 
              padding: "10px 15px",
              color: "white",
              textDecoration: "none",
              fontWeight: "600",
              fontSize: "15px",
              display: "block",
              transition: "all 0.3s ease"
            }}
          >
            {children}
          </RouterLink>
        </li>
      );
    }
  };

  return (
    <Wrapper className="animate" sidebarOpen={sidebarOpen}>
      <SidebarHeader className="flexSpaceCenter">
        <div className="flexNullCenter">
          <RouterLink className="pointer flexNullCenter" to="/" onClick={() => toggleSidebar()}>
            <SidebarLogo src={LogoIcon} height={100} width={100} alt="logo"/>
          </RouterLink>
        </div>
      </SidebarHeader>

      <CloseBtn 
        onClick={toggleSidebar} 
        className="animate pointer"
        aria-label="Close navigation menu"
      >
        <CloseIcon />
      </CloseBtn>

      <UlStyle className="flexNullCenter flexColumn">
        <SidebarNavItem to="home">Home</SidebarNavItem>
        <SidebarNavItem to="about">About</SidebarNavItem>
        <SidebarNavItem to="services">Services</SidebarNavItem>
        <li className="semiBold font15 pointer">
          <SidebarLaunchEventText 
            to="/developer-tools" 
            onClick={toggleSidebar}
            style={{
              color: '#079487',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            Developer Tools
          </SidebarLaunchEventText>
        </li>
        <SidebarNavItem to="impact">Impact</SidebarNavItem>
        <SidebarNavItem to="partners">Partners</SidebarNavItem>
        <li className="semiBold font15 pointer">
          <SidebarLaunchEventText to="/launch-event" onClick={toggleSidebar}>
            <FaRocket style={{ marginRight: '8px', fontSize: '14px' }} />
            Anton Tech Official Launch Event
          </SidebarLaunchEventText>
        </li>
        <SidebarNavItem to="contact">Contact</SidebarNavItem>
        <li className="semiBold font15 pointer">
          <SidebarLinkPrimary 
            href="https://app.anton.co.bw/login" 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={toggleSidebar}
          >
            Access Project Anton
          </SidebarLinkPrimary>
        </li>
      </UlStyle>
    </Wrapper>
  );
}

const Wrapper = styled.nav`
  width: 400px;
  height: 100vh;
  position: fixed;
  top: 0;
  padding: 0;
  right: ${(props) => (props.sidebarOpen ? "0px" : "-400px")};
  z-index: 9999;
  background: linear-gradient(135deg, 
    rgba(7, 148, 135, 0.95) 0%, 
    rgba(27, 94, 32, 0.9) 25%,
    rgba(7, 148, 135, 0.9) 75%,
    rgba(25, 118, 210, 0.95) 100%
  );
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 15% 85%, rgba(7, 148, 135, 0.2) 0%, transparent 40%),
      radial-gradient(circle at 85% 15%, rgba(7, 148, 135, 0.2) 0%, transparent 40%),
      radial-gradient(circle at 50% 50%, rgba(25, 118, 210, 0.1) 0%, transparent 60%);
    z-index: 1;
  }
  
  /* Ensure content is above background effects */
  & > * {
    position: relative;
    z-index: 2;
  }
  
  /* Mobile-specific improvements for better text visibility */
  @media (max-width: 768px) {
    width: 100%;
    background: rgba(7, 148, 135, 0.98) !important;
    backdrop-filter: blur(5px);
    transition: right 0.3s ease;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    opacity: 0.85;
    padding: 0;
    
    &::before {
      display: none;
    }
    
    /* Prevent any background changes on interaction */
    &:hover, &:active, &:focus {
      background: rgba(7, 148, 135, 0.98) !important;
      opacity: 0.85;
    }
  }
  
  /* Hide sidebar on desktop */
  @media (min-width: 1025px) {
    display: none;
  }
  
  @media (max-width: 400px) {
    width: 100%;
  }
`;

const SidebarHeader = styled.div`
  display: none;
  padding: 20px 0;
  
  /* Mobile-specific logo styling */
  @media (max-width: 768px) {
    padding: 25px 0;
    
    img {
      height: 120px !important;
      width: 120px !important;
      filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
    }
  }
`;

const SidebarLogo = styled.img`
  display: none;
`;

const CloseBtn = styled.button`
  border: none;
  outline: none;
  background-color: transparent;
  padding: 12px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  width: 44px;
  height: 44px;
  font-size: 24px;
  
  svg {
    width: 24px;
    height: 24px;
    stroke: white;
    stroke-width: 2;
  }
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.9);
    background-color: rgba(255, 255, 255, 0.3);
  }
`;

const UlStyle = styled.ul`
  padding: 40px 30px;
  li {
    margin: 20px 0;
  }
  
  /* Enhanced mobile text visibility */
  @media (max-width: 768px) {
    padding: 60px 20px 40px 20px;
    min-height: calc(100vh - 140px);
    
    li {
      margin: 15px 0;
      
      a, .whiteColor {
        color: white !important;
        font-weight: 700 !important;
        font-size: 16px !important;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
        padding: 12px 0 !important;
        display: block !important;
        text-decoration: none;
        border-bottom: 1px solid rgba(255, 255, 255, 0.15);
        background: transparent !important;
        background-color: transparent !important;
        transition: none !important;
        
        &:hover, &:active, &:focus, &:visited {
          color: rgba(255, 255, 255, 0.9) !important;
          text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.7);
          transform: none !important;
          background: transparent !important;
          background-color: transparent !important;
          opacity: 1 !important;
        }
      }
    }
  }
`;

const SidebarLaunchEventText = styled(RouterLink)`
  padding: 10px 15px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  display: block;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    color: #4CAF50;
    text-shadow: 0 0 25px rgba(76, 175, 80, 1), 0 0 35px rgba(76, 175, 80, 0.8);
    transform: scale(1.05);
  }
`;

const SidebarLinkPrimary = styled.a`
  color: white;
  text-decoration: none;
  padding: 12px 20px;
  background: linear-gradient(135deg, rgba(7, 148, 135, 0.8) 0%, rgba(7, 148, 135, 0.9) 100%);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  display: block;
  text-align: center;
  transition: all 0.3s ease;
  font-weight: 700;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #079487 0%, #0a7c73 100%);
    box-shadow: 0 8px 25px rgba(7, 148, 135, 0.6), 0 0 40px rgba(7, 148, 135, 0.4);
    border-color: rgba(255, 255, 255, 1);
    animation: none; /* Stop pulsing on hover */
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  /* Mobile-specific styling for better visibility */
  @media (max-width: 768px) {
    padding: 15px 20px;
    background: rgba(7, 148, 135, 0.9);
    border: 2px solid rgba(255, 255, 255, 0.6);
    font-size: 16px;
    font-weight: 800;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
    
    &:hover, &:active, &:focus {
      background: rgba(7, 148, 135, 1);
      border-color: rgba(255, 255, 255, 0.8);
      transform: none;
      box-shadow: none;
    }
    
    &::before {
      display: none;
    }
  }
`;